import _ from 'lodash';
import * as React from 'react';
import {useContext} from 'react';
import {SwiperClass} from 'swiper/react';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {SPECS} from '../../../specs';
import {IMediaItem, IVideoPlayer} from '../../../types/app-types';
import s from './ProductGalleryProvider.scss';

export interface ProductGalleryProviderProps extends ProvidedGlobalProps {
  imageRatioId: number;
  media: IMediaItem[];
  withImageRatio: boolean;
}

interface ProductGalleryProviderState {
  selectedIndex: number;
  mainMediaSwiper: SwiperClass;
  videoPlayers: Record<number, IVideoPlayer>;
  hasSelectedIndexChanged: boolean;
}

export interface IProductGalleryContext {
  selectedIndex: number;
  registerVideoPlayer(player: IVideoPlayer, index: number): void;
  changeSelectedIndex(selectedIndex: number): void;
  navigateLeft(): void;
  navigateRight(): void;
  hasSelectedIndexChanged: boolean;
  imageRatioId: number;
  setMainMediaSwiper(swiper: SwiperClass): void;
  mainMediaSwiper: SwiperClass;
}

export const ProductGalleryContext = React.createContext({
  selectedIndex: 0,
  registerVideoPlayer: _.noop,
  changeSelectedIndex: _.noop,
  navigateLeft: _.noop,
  navigateRight: _.noop,
  hasSelectedIndexChanged: false,
  imageRatioId: -1,
  setMainMediaSwiper: _.noop,
  mainMediaSwiper: {} as SwiperClass,
} as IProductGalleryContext);

export const useProductGallery = () => useContext(ProductGalleryContext);

class ProductGalleryProviderComponent extends React.Component<
  ProductGalleryProviderProps,
  ProductGalleryProviderState
> {
  public state = {
    selectedIndex: 0,
    hasSelectedIndexChanged: false,
    mainMediaSwiper: null,
    videoPlayers: {},
  };

  private readonly registerVideoPlayer = (player: IVideoPlayer, index: number) => {
    const {videoPlayers} = this.state;
    videoPlayers[index] = player;
    this.setState({videoPlayers});
  };

  private readonly changeSelectedIndex = (newlySelectedIndex: number) => {
    const {videoPlayers, mainMediaSwiper, selectedIndex} = this.state;
    const isMainMediaNavArrowsEnabled = this.props.globals.useExperiments.enabled(
      SPECS.ProductPageMainMediaNavigationArrows
    );
    if (isMainMediaNavArrowsEnabled && selectedIndex === newlySelectedIndex) {
      return;
    }
    if (videoPlayers[selectedIndex]) {
      videoPlayers[selectedIndex].pause();
    }
    this.setState({selectedIndex: newlySelectedIndex});
    this.setState({hasSelectedIndexChanged: true});

    if (isMainMediaNavArrowsEnabled) {
      if (mainMediaSwiper && mainMediaSwiper.realIndex !== newlySelectedIndex) {
        mainMediaSwiper.slideToLoop(newlySelectedIndex);
      }
    } else {
      mainMediaSwiper?.slideTo(newlySelectedIndex);
    }
    if (videoPlayers[newlySelectedIndex]) {
      videoPlayers[newlySelectedIndex].play();
    }
  };

  private readonly slideNext = () => {
    const {media} = this.props;
    const {selectedIndex} = this.state;
    this.changeSelectedIndex((selectedIndex + 1) % media.length);
  };

  private readonly slidePrev = () => {
    const {media} = this.props;
    const {selectedIndex} = this.state;
    this.changeSelectedIndex((selectedIndex - 1 + media.length) % media.length);
  };

  private readonly setMainMediaSwiper = (mainMediaRef: SwiperClass) => {
    const {mainMediaSwiper} = this.state;
    if (mainMediaRef && mainMediaSwiper !== mainMediaRef) {
      this.setState({mainMediaSwiper: mainMediaRef});
    }
  };

  public render(): JSX.Element {
    const {media, children, imageRatioId} = this.props;
    const {isRTL} = this.props.globals;
    const {selectedIndex, hasSelectedIndexChanged, mainMediaSwiper} = this.state;

    const composite = (
      <div data-hook="product-gallery-composite" className={s.root}>
        {children}
      </div>
    );

    return media ? (
      <ProductGalleryContext.Provider
        value={{
          selectedIndex,
          registerVideoPlayer: this.registerVideoPlayer,
          changeSelectedIndex: this.changeSelectedIndex,
          navigateLeft: isRTL ? this.slideNext : this.slidePrev,
          navigateRight: isRTL ? this.slidePrev : this.slideNext,
          hasSelectedIndexChanged,
          mainMediaSwiper,
          setMainMediaSwiper: this.setMainMediaSwiper,
          imageRatioId,
        }}>
        {composite}
      </ProductGalleryContext.Provider>
    ) : (
      composite
    );
  }
}

export const ProductGalleryProvider = withGlobalProps(ProductGalleryProviderComponent);
